import {
    handleConversationInitialization,
    handleGetConversationBySid
} from "~/modules/Conversation/ConversationErrorUtils";
import { ConversationsSdkImpl } from "~/modules/conversations/ConversationsSdkImpl";
import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { checkReservationExists, getReservationsByTask } from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { Conversation, ConversationImpl } from "~/modules/Conversation";
import { throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";

































export class GetConversationByTask implements Action<Promise<Conversation>> {
    


    readonly #taskSid: string;

    constructor(taskSid: string) {
        this.#taskSid = taskSid;
    }

    async run(ctx: {}): Promise<Conversation> {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        const conversations = context.retrieveInstanceOf(ContextInstance.ConversationsSdkImpl, ConversationsSdkImpl);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        await taskRouter.init();
        const worker = taskRouter.worker;

        const reservations = getReservationsByTask(worker, this.#taskSid).filter(
            (reservation) =>
                reservation.status === "accepted" ||
                reservation.status === "wrapping" ||
                reservation.status === "pending"
        );
        const reservation = reservations[0];

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "GetConversationByTask",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.GetConversationByTaskCompleted,
            extra: {
                taskSid: this.#taskSid
            },
            logger
        });

        const conversationSid = reservation.task.attributes.conversationSid as string | undefined;

        if (!conversationSid) {
            const errorMsg = `GetConversationByTask: No conversationSid found for task ${this.#taskSid}`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.GetConversationByTaskCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid
            });
            return throwFlexSdkError(context)(
                ErrorCode.NoConversationSidFoundForTask,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        await handleConversationInitialization(conversations, {
            actionName: "GetConversationByTask",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.GetConversationByTaskCompleted,
            extra: {
                taskSid: this.#taskSid,
                conversationSid
            },
            logger
        });

        if (reservation.status === "accepted") {
            const conversation = await handleGetConversationBySid(conversations, conversationSid, context, {
                actionName: "GetConversationByTask",
                moduleName: "actions/Conversations",
                worker,
                analytics,
                context,
                event: EVENTS.GetConversationByTaskCompleted,
                extra: {
                    taskSid: this.#taskSid,
                    conversationSid
                },
                logger
            });
            return conversation;
        }
        try {
            
            const conversation = await conversations.peekConversationBySid(conversationSid);
            sendWorkerTrackingEvent(EVENTS.GetConversationByTaskCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                conversationSid
            });
            return new ConversationImpl(context, conversation);
        } catch (error) {
            const errorMsg = `GetConversationByTask: Failed to peek conversation by sid ${conversationSid}`;
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.GetConversationByTaskCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                taskSid: this.#taskSid,
                conversationSid
            });
            return throwFlexSdkError(context)(
                ErrorCode.FailedToPeekConversation,
                { severity: ErrorSeverity.Error },
                errorMsg,
                error
            );
        }
    }
}
